import React, { Fragment, useState } from "react";
import {
  useListContext,
  useNotify,
  useUnselectAll,
  usePermissions,
  useGetList,
} from "react-admin";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Tooltip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Divider,
  Typography,
} from "@mui/material";
import SmsIcon from "@mui/icons-material/Sms";

const getToken = () => JSON.parse(localStorage.getItem("token"));
const GENDERS = ["male", "female"];

const sendBulkSms = async (body) => {
  const res = await fetch(
    `${process.env.REACT_APP_URL}/admin/beneficiaries/sms/bulk`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.message || "Failed to send bulk SMS");
  }
  return res.json().catch(() => ({}));
};

// Bulk action shown in the Datagrid's selection toolbar: "Send SMS to selected"
export const BulkSmsSelectedAction = () => {
  const { selectedIds = [] } = useListContext();
  const unselectAll = useUnselectAll();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const isDisabled = !message.trim() || selectedIds.length === 0;

  const handleClose = () => {
    if (loading) return;
    setOpen(false);
    setMessage("");
  };

  const handleSend = async () => {
    setLoading(true);
    try {
      await sendBulkSms({ message: message.trim(), beneficiaryIds: selectedIds });
      notify("SMS queued for sending", { type: "success" });
      unselectAll("beneficiaries");
      handleClose();
    } catch (err) {
      notify(`Error sending SMS: ${err.message}`, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <Button
        size="small"
        startIcon={<SmsIcon />}
        onClick={() => setOpen(true)}
        disabled={selectedIds.length === 0}
      >
        Send SMS to Selected
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Send SMS to {selectedIds.length} beneficiary(ies)
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            label="Message"
            placeholder="Type your message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            sx={{ mt: 1 }}
          />
          {selectedIds.length === 0 && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
              Select at least one beneficiary
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSend} disabled={isDisabled || loading}>
            {loading ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

// Standalone toolbar entry point: "Send SMS by Filter" — targets beneficiaries
// matching state/locality/gender/crop/service criteria (active ones only, per backend).
export const BulkSmsByFilterButton = () => {
  const { permissions } = usePermissions();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [stateId, setStateId] = useState("");
  const [localityId, setLocalityId] = useState("");
  const [gender, setGender] = useState("");
  const [cropIds, setCropIds] = useState([]);
  const [serviceIds, setServiceIds] = useState([]);

  const { data: states } = useGetList("state", { pagination: { page: 1, perPage: 500 } });
  const { data: localities } = useGetList("locality", { pagination: { page: 1, perPage: 500 } });
  const { data: crops } = useGetList("sub_category", { pagination: { page: 1, perPage: 500 } });
  const { data: services } = useGetList("servicetypes", { pagination: { page: 1, perPage: 500 } });

  if (!["admin", "operater"].includes(permissions)) return null;

  const hasTargeting =
    !!stateId || !!localityId || !!gender || cropIds.length > 0 || serviceIds.length > 0;
  const isDisabled = !message.trim() || !hasTargeting;
  const disableReason = !message.trim()
    ? "Message cannot be empty"
    : "Choose at least one of state, locality, gender, crop or service";

  const handleClose = () => {
    if (loading) return;
    setOpen(false);
    setMessage("");
    setStateId("");
    setLocalityId("");
    setGender("");
    setCropIds([]);
    setServiceIds([]);
  };

  const handleSend = async () => {
    setLoading(true);
    try {
      const body = {
        message: message.trim(),
        ...(stateId ? { state_id: [Number(stateId)] } : {}),
        ...(localityId ? { locality_id: [Number(localityId)] } : {}),
        ...(gender ? { gender: [gender] } : {}),
        ...(cropIds.length ? { crop_ids: cropIds.map(Number) } : {}),
        ...(serviceIds.length ? { service_ids: serviceIds.map(Number) } : {}),
      };
      await sendBulkSms(body);
      notify("SMS queued for sending", { type: "success" });
      handleClose();
    } catch (err) {
      notify(`Error sending SMS: ${err.message}`, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <Button size="small" startIcon={<SmsIcon />} onClick={() => setOpen(true)}>
        Send SMS by Filter
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Send Bulk SMS by Filter</DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 1 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Only active beneficiaries matching the selected criteria will receive this message.
            At least one filter is required.
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: 2,
              mb: 2,
            }}
          >
            <FormControl size="small">
              <InputLabel>State</InputLabel>
              <Select value={stateId} label="State" onChange={(e) => setStateId(e.target.value)}>
                <MenuItem value="">Any</MenuItem>
                {(states || []).map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>Locality</InputLabel>
              <Select value={localityId} label="Locality" onChange={(e) => setLocalityId(e.target.value)}>
                <MenuItem value="">Any</MenuItem>
                {(localities || []).map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    {l.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>Gender</InputLabel>
              <Select value={gender} label="Gender" onChange={(e) => setGender(e.target.value)}>
                <MenuItem value="">Any</MenuItem>
                {GENDERS.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>Crops</InputLabel>
              <Select
                multiple
                value={cropIds}
                label="Crops"
                onChange={(e) => setCropIds(e.target.value)}
                renderValue={(selected) =>
                  (crops || [])
                    .filter((c) => selected.includes(c.id))
                    .map((c) => c.name)
                    .join(", ")
                }
              >
                {(crops || []).map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>Services</InputLabel>
              <Select
                multiple
                value={serviceIds}
                label="Services"
                onChange={(e) => setServiceIds(e.target.value)}
                renderValue={(selected) =>
                  (services || [])
                    .filter((s) => selected.includes(s.id))
                    .map((s) => s.name)
                    .join(", ")
                }
              >
                {(services || []).map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Message
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            placeholder="Type your message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          {isDisabled && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
              {disableReason}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Tooltip title={isDisabled ? disableReason : ""}>
            <span>
              <Button variant="contained" onClick={handleSend} disabled={isDisabled || loading}>
                {loading ? "Sending..." : "Send"}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};
